import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Cấu hình trang "Tạo mới": layout + ẩn/hiện + thứ tự 4 card.
// Lưu localStorage. Mặc định: hiển thị cả 4 card, layout lưới to.

export const CREATE_CARD_KEYS = ['dichvu', 'sanpham', 'combo', 'goidichvu'];

const DEFAULT_CARDS = [
    { key: 'dichvu', visible: true, order: 0 },
    { key: 'sanpham', visible: true, order: 1 },
    { key: 'combo', visible: true, order: 2 },
    { key: 'goidichvu', visible: true, order: 3 },
];

// Đảm bảo luôn đủ 4 key hợp lệ (bỏ key lạ, bổ sung key thiếu xuống cuối).
function sanitizeCards(cards) {
    if (!Array.isArray(cards)) return DEFAULT_CARDS;
    const valid = cards.filter((c) => c && CREATE_CARD_KEYS.includes(c.key));
    const seen = new Set();
    const result = [];
    [...valid]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .forEach((c) => {
            if (seen.has(c.key)) return;
            seen.add(c.key);
            result.push({ key: c.key, visible: c.visible !== false, order: result.length });
        });
    CREATE_CARD_KEYS.forEach((key) => {
        if (!seen.has(key)) result.push({ key, visible: true, order: result.length });
    });
    return result;
}

const useCreateCardsStore = create(
    persist(
        (set, get) => ({
            layout: 'list', // 'list' | 'grid' | 'compact'
            cards: DEFAULT_CARDS,

            setLayout: (layout) => set({ layout }),

            toggleVisible: (key) =>
                set((s) => ({
                    cards: s.cards.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)),
                })),

            // dir: -1 lên, +1 xuống — đổi chỗ với card kề bên theo thứ tự.
            moveCard: (key, dir) => {
                const sorted = [...get().cards].sort((a, b) => a.order - b.order);
                const idx = sorted.findIndex((c) => c.key === key);
                const swap = idx + dir;
                if (idx < 0 || swap < 0 || swap >= sorted.length) return;
                [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
                set({ cards: sorted.map((c, i) => ({ ...c, order: i })) });
            },

            resetCards: () => set({ cards: DEFAULT_CARDS, layout: 'list' }),
        }),
        {
            name: 'createCardsPrefs',
            storage: createJSONStorage(() => localStorage),
            merge: (persisted, current) => ({
                ...current,
                ...(persisted || {}),
                cards: sanitizeCards(persisted?.cards),
            }),
        },
    ),
);

// Trả về cards theo thứ tự; mặc định chỉ lấy card đang hiện.
export function selectOrderedCards(cards, { includeHidden = false } = {}) {
    return [...cards]
        .sort((a, b) => a.order - b.order)
        .filter((c) => includeHidden || c.visible);
}

export default useCreateCardsStore;
